import React, { useState, useEffect, useCallback } from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import MenuMobilePublic from './MenuMobilePublic';
import { KaneaLogo } from '../../components/Common/KaneaLogo';
import {
  BookOpen, FileText, TrendingUp, ArrowRight, Lock,
  Calendar, Sparkles, ChevronRight, BarChart2, Globe
} from 'lucide-react';
import { API_URL } from '../../config';
import './LandingPage.css';
import './BlogPage.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PublicReport {
  id: number;
  title: string;
  report_type: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY';
  period_end: string | null;
  created_at: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_META: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  WEEKLY: { label: 'Hebdomadaire', color: '#0369a1', bg: '#e0f2fe', icon: <TrendingUp size={14} /> },
  MONTHLY: { label: 'Mensuel', color: '#6C3FC4', bg: '#ede9fe', icon: <BarChart2 size={14} /> },
  QUARTERLY: { label: 'Trimestriel', color: '#166534', bg: '#dcfce7', icon: <Globe size={14} /> },
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
};

const FILTER_TABS = [
  { id: 'ALL', label: 'Tous les rapports' },
  { id: 'WEEKLY', label: 'Hebdomadaires' },
  { id: 'MONTHLY', label: 'Mensuels' },
  { id: 'QUARTERLY', label: 'Trimestriels' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const BlogReportCard: React.FC<{ report: PublicReport }> = ({ report }) => {
  const meta = TYPE_META[report.report_type] || TYPE_META['WEEKLY'];

  return (
    <div className="blog-card">
      {/* badge + date */}
      <div className="blog-card__header">
        <span className="blog-card__badge" style={{ background: meta.bg, color: meta.color }}>
          {meta.icon}
          {meta.label}
        </span>
        {report.period_end && (
          <span className="blog-card__date">
            <Calendar size={12} />
            {formatDate(report.period_end)}
          </span>
        )}
      </div>

      {/* title */}
      <h3 className="blog-card__title">{report.title}</h3>

      {/* locked teaser */}
      <div className="blog-card__teaser">
        <Lock size={13} />
        <span>Accès réservé aux membres — Créez un compte gratuit pour lire.</span>
      </div>

      {/* CTA */}
      <div className="blog-card__actions">
        <Link to="/members/signup" className="blog-card__cta-primary">
          Lire le rapport <ChevronRight size={15} />
        </Link>
        <Link to="/login" className="blog-card__cta-ghost">
          Se connecter
        </Link>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const BlogPage: React.FC = () => {
  usePageTitle("Publications — rapports sur la BRVM");
  const [reports, setReports] = useState<PublicReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState('ALL');

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url =
        activeFilter === 'ALL'
          ? `${API_URL}/public/reports?limit=24`
          : `${API_URL}/public/reports?report_type=${activeFilter}&limit=24`;

      const res = await fetch(url);
      if (!res.ok) throw new Error('Erreur serveur');
      const json = await res.json();
      setReports(json.data || []);
    } catch {
      setError("Impossible de charger les publications. Réessayez plus tard.");
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  return (
    <div className="blog-page">

      {/* ── Header / Hero ─────────────────────────────────────────────── */}
      <header className="blog-hero">
        <nav className="landing-nav">
          <Link to="/home" className="landing-nav-logo">
            <KaneaLogo variant="horizontal" title="KaneaBourse" markColor="#6C3FC4" textColor="var(--lp-text)" height={28} />
            <div className="landing-nav-logo-dot" />
          </Link>
          <ul className="landing-nav-links">
            <li><Link to="/home">Accueil</Link></li>
            <li><Link to="/blog">Publications</Link></li>
            <li><Link to="/help">Centre d'aide</Link></li>
          </ul>
          <div className="landing-nav-actions">
            <Link to="/login" className="btn-nav-login">Se connecter</Link>
            <Link to="/members/signup" className="btn-nav-signup">Compte gratuit</Link>
            <MenuMobilePublic liens={[{ label: "Accueil", to: '/home' }, { label: "Tarifs", to: '/home#tarifs' }, { label: "Outils gratuits", to: '/outils' }, { label: "Publications", to: '/blog' }, { label: "Centre d'aide", to: '/help' }]} />
          </div>
        </nav>

        <div className="blog-hero__content">
          <span className="blog-hero__pill">
            <Sparkles size={14} />
            Analyses par Nia, notre IA financière
          </span>
          <h1 className="blog-hero__title">
            Publications&nbsp;<span className="blog-hero__accent">Kanea</span>
          </h1>
          <p className="blog-hero__sub">
            Rapports hebdomadaires, mensuels et trimestriels sur la&nbsp;
            <strong>BRVM</strong>&nbsp;— rédigés et revus par notre équipe d'analystes.
          </p>
          <Link to="/members/signup" className="blog-hero__btn">
            Créer un compte gratuit <ArrowRight size={17} />
          </Link>
        </div>

        {/* Floating stat cards */}
        <div className="blog-hero__stats">
          {[
            { label: 'Publications', value: '100+' },
            { label: 'Valeurs BRVM', value: '48' },
            { label: 'Années de data', value: '5' },
          ].map((s) => (
            <div key={s.label} className="blog-stat">
              <span className="blog-stat__value">{s.value}</span>
              <span className="blog-stat__label">{s.label}</span>
            </div>
          ))}
        </div>
      </header>

      {/* ── Filter Tabs ───────────────────────────────────────────────── */}
      <div className="blog-filters">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.id}
            className={`blog-filter-tab${activeFilter === tab.id ? ' blog-filter-tab--active' : ''}`}
            onClick={() => setActiveFilter(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Content grid ──────────────────────────────────────────────── */}
      <main className="blog-main">
        {loading ? (
          <div className="blog-loading">
            <div className="blog-spinner" />
            <span>Chargement des publications…</span>
          </div>
        ) : error ? (
          <div className="blog-error">
            <FileText size={40} />
            <p>{error}</p>
            <button className="blog-retry" onClick={fetchReports}>Réessayer</button>
          </div>
        ) : reports.length === 0 ? (
          <div className="blog-empty">
            <BookOpen size={48} />
            <p>Aucune publication pour cette sélection.</p>
          </div>
        ) : (
          <div className="blog-grid">
            {reports.map((r) => <BlogReportCard key={r.id} report={r} />)}
          </div>
        )}
      </main>

      {/* ── Conversion Banner ─────────────────────────────────────────── */}
      <section className="kanea-purple-banner">
        <div className="kanea-purple-banner__inner">
          <h2 className="kanea-purple-banner__title">
            Accédez à tous les rapports + analyses IA en temps réel
          </h2>
          <p className="kanea-purple-banner__sub">
            Rejoignez les investisseurs qui utilisent Kanea pour analyser la BRVM
          </p>
          <div className="kanea-purple-banner__actions">
            <Link to="/members/signup" className="kanea-purple-banner__cta">
              Créer un compte gratuit <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="kanea-purple-banner__ghost">
              Déjà membre ? Connexion
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="landing-footer">
        <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 24, textAlign: 'center', fontSize: '0.75rem', color: '#64748B', borderBottom: '1px solid var(--lp-border)', marginBottom: 24 }}>
          <strong>Avertissement légal :</strong> KaneaBourse est une plateforme d'analyse de données et d'outils d'aide à la décision. Nous ne sommes ni une SGI (Société de Gestion et d'Intermédiation) ni un Conseiller en Investissement Financier agréé par l'AMF-UMOA. Les informations, analyses, et valeurs fondamentales fournies (y compris par l'IA) le sont à titre strictement informatif et éducatif. Elles ne constituent en aucun cas une recommandation d'achat ou de vente personnalisée. Tout investissement en bourse comporte un risque de perte en capital.
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div className="landing-footer-copy">
            © {new Date().getFullYear()} KaneaBourse — Tous droits réservés
          </div>
          <div className="landing-footer-links" style={{ display: 'flex', gap: 16 }}>
            <Link to="/terms">Conditions d'utilisation</Link>
            <Link to="/confidentialite">Confidentialité</Link>
            <a href="mailto:contact@kaneabourse.com">Contact</a>
            <a href="https://www.brvm.org" target="_blank" rel="noopener noreferrer">BRVM</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
